import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart, Package, FileText, TrendingUp, TrendingDown,
  AlertTriangle, Clock, ArrowRight, Euro, BarChart3, Sparkles,
  Loader2, Sun, CheckCircle, AlertCircle, Calendar, Box
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import PushNotificationToggle from '@/components/PushNotificationToggle';

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendUp, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600', purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border-2 border-slate-100 p-6 hover:border-[#1e3a5f]/20 transition-colors">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 ${colors[color]} rounded-sm flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
            {trendUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span>{trend}%</span>
          </div>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-[#1e3a5f]">{value}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </motion.div>
  );
};

const DailyBriefing = ({ stats, t }) => {
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('briefing_good_morning');
    if (hour < 18) return t('briefing_good_afternoon');
    return t('briefing_good_evening');
  };

  const formatDate = () => {
    const lang = document.documentElement.lang || 'es';
    const localeMap = { es: 'es-ES', en: 'en-US', fr: 'fr-FR', de: 'de-DE', nl: 'nl-NL', hi: 'hi-IN', zh: 'zh-CN' };
    return new Date().toLocaleDateString(localeMap[lang] || 'es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const briefingData = {
    yesterday: { sales: 45, revenue: 892.50 },
    lowStock: stats?.low_stock_count || 3,
    verifactu: { synced: true, pending: 0 },
    tasks: [
      { id: 1, text: t('briefing_check_stock'), done: false, priority: 'high' },
      { id: 2, text: t('briefing_review_sales'), done: true, priority: 'medium' },
      { id: 3, text: t('briefing_prepare_orders'), done: false, priority: 'low' },
    ]
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] text-white p-6 col-span-full" data-testid="daily-briefing">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
            <Sun className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{t('briefing_title')}</h2>
            <p className="text-white/70 text-sm capitalize">{formatDate()}</p>
          </div>
        </div>
        <span className="text-2xl">{getGreeting()}!</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-white/70" />
            <span className="text-sm font-medium text-white/70">{t('briefing_yesterday')}</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-white/60 text-sm">{briefingData.yesterday.sales} {t('briefing_sales')}</span>
              <span className="font-bold">{'\u20AC'}{briefingData.yesterday.revenue.toFixed(2)}</span>
            </div>
            <div className="w-full bg-white/20 h-1 rounded"><div className="bg-[#22c55e] h-1 rounded" style={{ width: '75%' }} /></div>
            <p className="text-xs text-white/50">{t('briefing_revenue')}</p>
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Box className="w-5 h-5 text-white/70" />
            <span className="text-sm font-medium text-white/70">{t('briefing_restock')}</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-3xl font-bold">{briefingData.lowStock}</span>
              <p className="text-xs text-white/50">{t('briefing_products_low')}</p>
            </div>
            {briefingData.lowStock > 0 && (
              <Link to="/shop/stock?filter=low" className="text-xs text-[#22c55e] hover:underline">{t('briefing_view_all')} &rarr;</Link>
            )}
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-white/70" />
            <span className="text-sm font-medium text-white/70">{t('briefing_verifactu')}</span>
          </div>
          <div className="flex items-center gap-3">
            {briefingData.verifactu.synced ? (
              <>
                <CheckCircle className="w-8 h-8 text-[#22c55e]" />
                <div><p className="font-medium">{t('briefing_all_synced')}</p><p className="text-xs text-white/50">{t('briefing_no_issues')}</p></div>
              </>
            ) : (
              <>
                <AlertCircle className="w-8 h-8 text-amber-400" />
                <div><p className="font-medium">{briefingData.verifactu.pending} {t('briefing_pending_sync')}</p></div>
              </>
            )}
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-white/70" />
            <span className="text-sm font-medium text-white/70">{t('briefing_tasks')}</span>
          </div>
          <div className="space-y-2">
            {briefingData.tasks.map(task => (
              <div key={task.id} className="flex items-center gap-2 text-sm">
                {task.done ? <CheckCircle className="w-4 h-4 text-[#22c55e]" /> : (
                  <div className={`w-4 h-4 rounded-full border-2 ${task.priority === 'high' ? 'border-red-400' : task.priority === 'medium' ? 'border-amber-400' : 'border-white/40'}`} />
                )}
                <span className={task.done ? 'text-white/50 line-through' : 'text-white/90'}>{task.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ShopDashboard = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const shopsResponse = await api.get('/shops');
      if (shopsResponse.data.length === 0) { setNeedsSetup(true); setLoading(false); return; }
      const shopId = shopsResponse.data[0].id;
      const statsResponse = await api.get(`/shops/${shopId}/dashboard-stats`);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      if (error.response?.status === 404) setNeedsSetup(true);
    } finally { setLoading(false); }
  };

  if (loading) {
    return (<ShopLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>);
  }

  if (needsSetup) {
    return (
      <ShopLayout>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center py-16" data-testid="setup-needed-card">
          <div className="w-24 h-24 mx-auto mb-6 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
            <Sparkles className="w-12 h-12 text-[#22c55e]" />
          </div>
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">{t('dashboard_setup_needed')}</h2>
          <p className="text-slate-600 mb-8 text-lg">{t('dashboard_setup_desc')}</p>
          <Link to="/shop/setup" className="vf-btn-primary inline-flex items-center gap-2 text-lg px-8 py-4" data-testid="start-setup-btn">
            {t('dashboard_start_setup')} <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="space-y-6" data-testid="shop-dashboard">
        <DailyBriefing stats={stats} t={t} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title={t('dashboard_today_sales')} value={stats?.today_sales || 0} subtitle={t('dashboard_vs_yesterday')} icon={ShoppingCart} color="blue" />
          <StatCard title={t('dashboard_today_revenue')} value={`\u20AC${(stats?.today_revenue || 0).toFixed(2)}`} subtitle={t('dashboard_this_week')} icon={Euro} trend={12} trendUp={true} color="green" />
          <StatCard title={t('dashboard_total_products')} value={stats?.products_count || 0} icon={Package} color="purple" />
          <StatCard title={t('dashboard_low_stock')} value={stats?.low_stock_count || 0} icon={AlertTriangle} color="amber" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white border-2 border-slate-100 p-6">
            <h3 className="font-semibold text-[#1e3a5f] mb-4">{t('dashboard_quick_actions')}</h3>
            <div className="space-y-3">
              <Link to="/shop/pos" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 transition-colors rounded-sm" data-testid="quick-action-pos">
                <div className="w-10 h-10 bg-[#22c55e] rounded-sm flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-white" /></div>
                <div className="flex-1"><p className="font-medium text-[#1e3a5f]">{t('dashboard_open_pos')}</p></div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </Link>
              <Link to="/shop/products/new" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 transition-colors rounded-sm">
                <div className="w-10 h-10 bg-purple-500 rounded-sm flex items-center justify-center"><Package className="w-5 h-5 text-white" /></div>
                <div className="flex-1"><p className="font-medium text-[#1e3a5f]">{t('dashboard_new_product')}</p></div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </Link>
              <Link to="/shop/invoices/new" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 transition-colors rounded-sm">
                <div className="w-10 h-10 bg-blue-500 rounded-sm flex items-center justify-center"><FileText className="w-5 h-5 text-white" /></div>
                <div className="flex-1"><p className="font-medium text-[#1e3a5f]">{t('dashboard_create_invoice')}</p></div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </Link>
              <Link to="/shop/reports" className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 transition-colors rounded-sm">
                <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center"><BarChart3 className="w-5 h-5 text-white" /></div>
                <div className="flex-1"><p className="font-medium text-[#1e3a5f]">{t('dashboard_view_reports')}</p></div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="lg:col-span-2 bg-white border-2 border-slate-100 p-6">
            <div className="mb-4">
              <PushNotificationToggle token={localStorage.getItem('access_token')} />
            </div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#1e3a5f]">{t('dashboard_recent_sales')}</h3>
              <Link to="/shop/reports" className="text-sm text-[#22c55e] hover:underline">{t('dashboard_view_all')}</Link>
            </div>
            {(stats?.today_sales || 0) === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                  <ShoppingCart className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-500 mb-4">{t('dashboard_no_sales_yet')}</p>
                <Link to="/shop/pos" className="text-[#22c55e] hover:underline font-medium">{t('dashboard_start_selling')} &rarr;</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-sm flex items-center justify-center"><Clock className="w-5 h-5 text-[#1e3a5f]" /></div>
                      <div>
                        <p className="font-medium text-[#1e3a5f]">{t('pos_sale_number')}{1000 + i}</p>
                        <p className="text-xs text-slate-500">{i * 10} min</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#22c55e]">{'\u20AC'}{(Math.random() * 50 + 10).toFixed(2)}</p>
                      <p className="text-xs text-slate-500">{Math.floor(Math.random() * 5 + 1)} {t('pos_items')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </ShopLayout>
  );
};

export default ShopDashboard;
